/**
 * §4 — what this widget is set up to do, as a table of facts.
 *
 * `.plans/ai-product-placer/README.md` §3.3: the client "reads it and the studio sets
 * it" in v1; v1.1 lets an owner or an admin edit the keys the schema marks
 * `x-editor: client`. So this is a `Kv` and **one** button, and the fourth section of
 * a page that had three.
 *
 * **The button is absent rather than disabled** for a schema with no client keys, and
 * for a `member` or a `billing` seat — `usage.setConfigByClient` refuses both with
 * `assertMayManageWidget`, which is the same `money:approve` rule pausing and the cap
 * are on. `WidgetConfigForm.tsx` is what it opens, and everything about drawing and
 * validating a schema lives there.
 *
 * **It is drawn from the widget's own snapshotted schema, and never from
 * `widget_type`.** The labels are the schema's `title`s and the order is the schema's
 * own, so a widget kind that gains a property gains a row here with no code — which
 * is the same promise `usage.test.ts` makes on the API side and which the
 * source-scanning test in `WidgetPage.test.tsx` keeps honest.
 *
 * Three shapes and one refusal:
 *
 *   - `config_schema === null` — the kind takes no configuration, which is what the
 *     chatbot and the booking widget are. Nothing is rendered at all, so their pages
 *     are exactly what they were.
 *   - `config_version === 0` — configured by nobody yet. One sentence, no table: a
 *     table of eight blanks says less than a sentence saying why.
 *   - otherwise, one row per property.
 *
 * **A string array is not a `Kv` value.** `Kv.tsx` records §5's refusal of
 * `.kv--prose` — "`.kv` is a money column; four sentences are not a column" — and
 * names the alternative it sanctions: a `Stack gap="tight"` of `<Small>`. Origins and
 * product hosts are lists of six-word strings and go there, under the table, titled
 * from the same schema.
 */

import { useMemo, useState, type ReactNode } from 'react'

import { configFieldsOrNull, type ConfigField, type WidgetView } from '@fareast/shared'

import { usePrincipal } from '../../../auth/principal.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Kv, KvRow } from '../../../components/portal/Kv.js'
import { Region } from '../../../components/portal/Region.js'
import { Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { B, Muted, Small } from '../../../components/portal/Text.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import { abilitiesOf } from '../settings/roles.js'
import { editableConfigOf, WidgetConfigForm } from './WidgetConfigForm.js'

export type WidgetConfigPanelProps = {
  readonly widget: WidgetView
}

export function WidgetConfigPanel({ widget }: WidgetConfigPanelProps) {
  const principal = usePrincipal()
  const [editing, setEditing] = useState(false)

  /*
   * `configFieldsOrNull`, not `configFieldsOf`.
   *
   * A schema this system cannot read should be impossible — `assertConfigSchemaShape`
   * runs on both catalog actions and a unit test walks the seeds — but the refusal it
   * raises is a `DomainError`, and a throwing validator on a render path turns one bad
   * row into a client's whole widget page going dark. A section that is not there is
   * the smaller failure, and the row is a studio bug rather than a client's.
   */
  const schema = widget.config_schema
  const fields = useMemo(() => (schema === null ? null : configFieldsOrNull(schema)), [schema])

  /*
   * What an owner or an admin may change here, from the same reader.
   *
   * Memoised because it is the resolver's schema as well as the form's fields, and a
   * fresh object every render would rebuild both on every keystroke.
   */
  const editable = useMemo(
    () => (schema === null || fields === null ? null : editableConfigOf(schema, fields)),
    [schema, fields],
  )

  if (schema === null || fields === null) return null

  /*
   * The same rule the API applies, read from the same table.
   *
   * `usage.setConfigByClient` calls `assertMayManageWidget`, which is `money:approve`
   * — "an owner or an admin may, a `member` may not". `.docs/19` §Edge cases asks for
   * the control to be **absent** rather than disabled, which is what pausing and the
   * cap on the same subject already do.
   */
  const mayEdit = abilitiesOf(principal).approvesMoney && editable !== null

  return (
    <Section>
      {/*
        The note used to read "Set by the studio", which was the whole truth in v1 and
        is half of it now: a schema that marks a property `x-editor: client` says some
        of this is the account's own (`README.md` §3.3, v1.1). The words follow the
        **schema** rather than the reader, because who set a setting does not change
        with who is looking at it.
      */}
      <SectionHead
        title="How it is set up"
        note={editable === null ? 'Set by the studio' : 'Set by the studio, apart from yours'}
        pushNote
      />

      <Region>
        {widget.config_version === 0 ? (
          <Empty>Not configured yet — the studio sets this up when the widget goes live.</Empty>
        ) : editing && editable !== null ? (
          <WidgetConfigForm
            widget={widget}
            editable={editable}
            onClose={() => {
              setEditing(false)
            }}
          />
        ) : (
          <>
            <WidgetConfigFacts config={widget.config ?? {}} fields={fields} />

            {mayEdit && widget.config_version > 0 ? (
              <ButtonRow>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditing(true)
                  }}
                >
                  Change your settings
                </Button>
              </ButtonRow>
            ) : null}
          </>
        )}
      </Region>
    </Section>
  )
}

export type WidgetConfigFactsProps = {
  /** The stored configuration, `{}` when nothing is stored. */
  readonly config: Record<string, unknown>
  /** The schema's properties, in its own order (`configFieldsOf`). */
  readonly fields: readonly ConfigField[]
}

/**
 * The table of facts itself: one `Kv` row per scalar property, and each string array
 * as a titled stack under it (§5's sanctioned alternative to `.kv--prose`).
 *
 * Exported because session 46's Connect page states the same facts as a hosted
 * widget's "Site settings", in both portals, and a second drawing of one config
 * schema is the copy P5's DRY watchlist forbids.
 */
export function WidgetConfigFacts({ config, fields }: WidgetConfigFactsProps) {
  const format = useFormat()
  const rows = fields.filter((field) => field.type !== 'string_array')
  const lists = fields.filter((field) => field.type === 'string_array')

  return (
    <>
      <Kv>
        {rows.map((field) => (
          <KvRow key={field.key} k={field.title} v={valueOf(field, config, format)} />
        ))}
      </Kv>

      {lists.map((field) => (
        <Stack key={field.key} gap="tight">
          <Small>
            <B>{field.title}</B>
          </Small>
          {entriesOf(config[field.key]).map((entry) => (
            <Small key={entry}>{entry}</Small>
          ))}
        </Stack>
      ))}
    </>
  )
}

/**
 * One value, in the words the client reads.
 *
 * A number goes through `useFormat` like every number on this page; a boolean is
 * "On" or "Off" rather than "true", which is a value and not a word; an enum and a
 * string are printed as they are, because the schema chose them. A key the stored
 * config does not carry prints an em dash — which happens only for an optional
 * property with no default, and saying nothing is better than saying "undefined".
 */
function valueOf(
  field: ConfigField,
  config: Record<string, unknown>,
  format: Formatter,
): ReactNode {
  const value = config[field.key]
  if (field.type === 'boolean' && typeof value === 'boolean') return value ? 'On' : 'Off'
  if (typeof value === 'number') return format.number(value)
  if (typeof value === 'string') return value
  // Anything else is a key the schema declares and the stored config does not carry,
  // which is an optional property with no default. An em dash says that; "undefined"
  // says the page is broken.
  return <Muted>—</Muted>
}

/** A string array, defensively: the portal validated it, and this renders it. */
function entriesOf(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.map((entry) => String(entry)) : []
}
